import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@jq4race/i18n';
import {
  type AssignEventStaffRequest,
  AssignEventStaffRequestSchema,
  type UserListItem,
} from '@jq4race/shared';
import { Button, Input, Label } from '@jq4race/ui';
import { TriangleAlert } from 'lucide-react';
import { forwardRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { translateApiError } from '../../../../shared/errors/translateApiError';

interface AssignStaffFormProps {
  eligibleUsers: UserListItem[];
  loadingUsers: boolean;
  onSubmit: (values: AssignEventStaffRequest) => Promise<void>;
  disabled?: boolean;
}

export function AssignStaffForm({
  eligibleUsers,
  loadingUsers,
  onSubmit,
  disabled,
}: AssignStaffFormProps) {
  const { t } = useTranslation('events');
  const [serverError, setServerError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<AssignEventStaffRequest>({
    resolver: zodResolver(AssignEventStaffRequestSchema),
    defaultValues: { userId: '', role: null },
  });

  async function handleFormSubmit(values: AssignEventStaffRequest) {
    setServerError(null);
    try {
      await onSubmit(values);
      reset({ userId: '', role: null });
    } catch (e) {
      setServerError(translateApiError(e, t, { fallback: t('assignStaffForm.genericError') }));
    }
  }

  const isDisabled = disabled || isSubmitting || loadingUsers;

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-3">
      {serverError && <FormBanner message={serverError} />}

      <div className="grid gap-4 sm:grid-cols-[2fr_1fr_auto] sm:items-end">
        <Field
          label={t('assignStaffForm.valet')}
          htmlFor="staff-user"
          error={errors.userId?.message}
        >
          <SelectInput id="staff-user" disabled={isDisabled} {...register('userId')}>
            <option value="">
              {loadingUsers ? t('assignStaffForm.loadingUsers') : t('assignStaffForm.selectValet')}
            </option>
            {eligibleUsers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.email ?? u.id}
              </option>
            ))}
          </SelectInput>
        </Field>

        <Field
          label={t('assignStaffForm.roleLabel.label')}
          htmlFor="staff-role"
          error={errors.role?.message}
        >
          <Input
            id="staff-role"
            placeholder={t('assignStaffForm.roleLabel.placeholder')}
            disabled={isDisabled}
            {...register('role', {
              setValueAs: (v: string) => (v === '' ? null : v),
            })}
          />
        </Field>

        <Button type="submit" size="sm" disabled={isDisabled}>
          {isSubmitting ? t('assignStaffForm.assigning') : t('assignStaffForm.assign')}
        </Button>
      </div>
    </form>
  );
}

function Field({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label htmlFor={htmlFor} className="mb-1.5 block text-[13px] font-medium text-jq-charcoal">
        {label}
      </Label>
      {children}
      {error && (
        <p role="alert" className="mt-1.5 text-[12px] text-jq-danger">
          {error}
        </p>
      )}
    </div>
  );
}

const SelectInput = forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(
  function SelectInput({ className, ...rest }, ref) {
    return (
      <select
        ref={ref}
        {...rest}
        className={`flex h-10 w-full rounded-sm border border-jq-sand bg-jq-surface-0 px-3 text-[14px] text-jq-charcoal shadow-sm transition focus:border-jq-charcoal focus:outline-none focus:ring-1 focus:ring-jq-charcoal disabled:cursor-not-allowed disabled:opacity-50 ${
          className ?? ''
        }`}
      />
    );
  },
);

function FormBanner({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-4 py-3 text-[13px] text-jq-danger"
    >
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
    </div>
  );
}
